import { useId, type ReactNode } from 'react';
import s from './ui.module.css';

/** Label + control + hint/error. The control receives the generated id through the render prop. */
export function Field({ label, hint, error, children }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className={s.field}>
      <label className={s.fieldLabel} htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {error ? <span className={s.fieldError}>{error}</span> : hint ? <span className={s.fieldHint}>{hint}</span> : null}
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: ReadonlyArray<{ value: T; label: ReactNode }>; onChange: (v: T) => void; label: string }) {
  return (
    <div className={s.segmented} role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Checkbox({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className={s.check}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  );
}

/** Server rule for every audited reason/note: 3–500 characters. */
export const REASON_MIN = 3;
export const REASON_MAX = 500;
export const reasonError = (v: string) => (v.trim().length < REASON_MIN ? `最少 ${REASON_MIN} 個字。` : v.length > REASON_MAX ? `最多 ${REASON_MAX} 個字。` : null);

export function ReasonField({ value, onChange, label = '原因', hint = '會記錄在審計日誌。', autoFocus, touched = true }: { value: string; onChange: (v: string) => void; label?: string; hint?: string; autoFocus?: boolean; touched?: boolean }) {
  const err = touched && value.length > 0 ? reasonError(value) : null;
  return (
    <Field label={label} hint={`${hint} ${value.length}/${REASON_MAX}`} error={err}>
      {(id) => <textarea id={id} value={value} maxLength={REASON_MAX} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus} rows={3} />}
    </Field>
  );
}
